import { FC, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { CommonStyle, ControlledForm, ControlledTable } from "Components";
import { RouteUrls } from "Constant/RouteUrls";
import CommonFormatter from "Utils/Common.Formatter";
import { TableLayout } from "Layouts";
import {
  actionArr,
  FormDataObj,
  TableCol,
} from "./Data";
import { useNavigate } from "react-router-dom";
import CstmBtn from "Components/CstmBtn";
import { DropdownArrPropsType, SingleSelectValType } from "DataTypes/Form";
import ProblemApi from "Service/Problem.api";
import ProbFormatter from "Utils/Prob.Formatter";
import { FormatDropdownData } from "Utils/DataFormatter";
import PreApprovedPlanApi from "Service/PreApprovedPlan.api";
import PreApprovedPlanFormatter from "Utils/PreApprovedPlan.Formatter";
import { CheckNumber } from "Utils/common";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading } from "Redux/Screen/Action";
import { UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { PreApprovedPlanListType } from "DataTypes/PatientManagement.type";
import { ModalLayout } from "Layouts";
import { DeleteButtonArray } from "Components/Modal/Data";
import { Typography } from "@mui/material";

const MyPreApprovedPlans: FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const [planList, setPlanList] = useState<PreApprovedPlanListType[]>([]);
  const [problemSearch, setProblemSearch] = useState("");
  const [problemDropArr, setProblemDropArr] =
    useState<DropdownArrPropsType[]>([]);
  const [problemDropVal, setProblemDropVal] =
    useState<SingleSelectValType>(null)
  const [deletePlan, setDeletePlan] =
    useState<PreApprovedPlanListType | null>(null);

  useEffect(() => {
    CommonFormatter.HandleNavArr({
      id: "myPreApprovedPlans",
      label: "My Pre-Approved Plan(s)",
      path: `/${RouteUrls.hs}/${RouteUrls.myPreApprovedPlans}`,
      level: 1,
      link: RouteUrls.myPreApprovedPlans,
      navType: "my-items",
    });
    handleProblemSearch();
    handlePlanSearch();
  }, []);

  const handleProblemSearch: (
    searchVal?: string
  ) => void = async (searchVal) => {
    const newSearchVal = searchVal || "";
    const res = await ProblemApi.list({
      limit: 20,
      offset: 0,
      data: { problemName: newSearchVal },
    });
    let newProbDrop: DropdownArrPropsType[] = [];
    if (res?.success) {
      const formattedData = ProbFormatter.List(res?.data);
      newProbDrop = FormatDropdownData(formattedData, "id", "name", "id");
    }
    setProblemDropArr(newProbDrop);
  }

  const handlePlanSearch: () => void = async () => {
    const payload = { createdBy: myInfo.id, practiceId: null }
    const res = await PreApprovedPlanApi.PlanList({ data: payload });
    if (res?.success) {
      const formattedData = PreApprovedPlanFormatter.PlanList(res?.data);
      setPlanList(formattedData);
    }
  }

  const handleEdit: (
    data: PreApprovedPlanListType
  ) => void = (data) => {
    navigate(`./${data.id}/${RouteUrls.plan}`);
  };

  const handleView: (
    data: PreApprovedPlanListType
  ) => void = (data) => {
    navigate(`./${data.id}/${RouteUrls.plan}`);
  };

  const handleDelete: (
    data: PreApprovedPlanListType
  ) => void = (data) => {
    setDeletePlan(data);
  };

  const handleDeleteConfirm: () => void = async () => {
    if (!deletePlan?.id) return;
    const res = await PreApprovedPlanApi.DeletePlan({ id1: deletePlan.id });
    if (res?.success) {
      handlePlanSearch();
    }
    setDeletePlan(null);
  };

  const handleCreatePlan: () => void = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Loading..." }))
    const res = await PreApprovedPlanApi.TemplateSearch(
      { id1: problemDropVal }
    );
    if (res.success) {
      const formattedData = PreApprovedPlanFormatter.TemplateSearch(res?.data);
      const payload = {
        practiceId: myInfo.practiceId,
        category: formattedData
      }
      const res2 = await PreApprovedPlanApi.CreatePlan(
        { id1: problemDropVal, data: payload }
      );
      if (res2.success) {
        const planId = CheckNumber(res2?.data?.id);
        navigate(`./${planId}/${RouteUrls.plan}`);
      }
    }
    dispatch(stopLoading());
  }


  const TblBodyJSX = (
    <ControlledTable.TblBody
      tableColumn={() => TableCol()}
      tableRow={planList}
      tableAction={(data: PreApprovedPlanListType) =>
        actionArr(
          () => handleView(data),
          () => handleEdit(data),
          () => handleDelete(data),
          data
        )
      }
    />
  );

  return (
    <Box height="91%" sx={{ ...CommonStyle.sxWhiteBox, overflow: "auto" }}>
      <ModalLayout
        modwidth="30%"
        heading={"Warning"}
        content={
          <Typography textAlign="center">
            Are you sure you want to delete this draft ?
          </Typography>
        }
        onClose={() => setDeletePlan(null)}
        open={deletePlan ? true : false}
        buttonArr={DeleteButtonArray(handleDeleteConfirm, () => {
          setDeletePlan(null);
        })}
      />
      <Box sx={{ p: 2 }}>
        <Box display="flex" mb={2} alignItems={"end"}>
          <Box width="30%" mr={2}>
            <ControlledForm.SingleApi
              {...FormDataObj}
              onClick={async (val) => {
                setProblemSearch(
                  problemDropArr.find(
                    findEl => findEl.value == val
                  )?.label || ""
                );
                setProblemDropVal(val)
              }}
              onChangeApiSearch={true}
              inputChange={async (val) => {
                setProblemSearch(val);
                setProblemDropVal(null);
                handleProblemSearch(val);
              }}
              inputVal={problemSearch}
              dropArr={problemDropArr}
              dropVal={problemDropVal}
              apiSearch={true}
              iconSearch={true}
            />
          </Box>
          <CstmBtn
            label="Create Pre-Approved Plan"
            onClick={handleCreatePlan}
            disable={!problemDropVal}
          />
        </Box>
        <TableLayout
          tblBody={TblBodyJSX}
          tblHead={ControlledTable.TblHead(TableCol())}
        />
      </Box>
    </Box>
  );
};
export default MyPreApprovedPlans;
